/**
 * Pricing Section V3 resolver — merges a Sanity `pricingSectionV3` document
 * over the code defaults (products.ts catalog, copy.ts strings, bundled
 * logos). Every field is optional in the doc; anything missing falls back, so
 * a half-filled document can never break the section.
 */

import type {
  GROQImage,
  IPricingSectionV3,
  IV3ChallengeCol,
  IV3CustomSpec,
  IV3FundedCol,
} from "@/sanity/lib/definitions";
import { resolveI18nText, type II18nText } from "@/sanity/globals/I18n";
import {
  Asset,
  CATALOG,
  Catalog,
  ChallengeCol,
  CustomSpec,
  FundedCol,
  PlatformId,
  Product,
  ProductId,
  RAPID_FUNDED_VARIANTS,
  RapidFundedVariants,
  SizeEntry,
  SizeId,
  VariantId,
} from "./products";
import { resolveNewPricingCopy, type NewPricingCopy } from "@/components/pageComponents/newPricing/copy";

const ASSETS: Asset[] = ["futures", "cfd_crypto"];
const PRODUCTS: ProductId[] = ["core", "rapid", "instant"];
const SIZES: SizeId[] = ["50k", "100k", "200k"];
const VARIANTS: VariantId[] = ["single", "mpl", "dll", "classic", "daily"];

const isAsset = (v: string | undefined): v is Asset => ASSETS.includes(v as Asset);
const isProduct = (v: string | undefined): v is ProductId => PRODUCTS.includes(v as ProductId);
const isSize = (v: string | undefined): v is SizeId => SIZES.includes(v as SizeId);
const isVariant = (v: string | undefined): v is VariantId => VARIANTS.includes(v as VariantId);

/* ── Platform selector ───────────────────────────────────────────────────── */

export interface ResolvedPlatformLogo {
  key: string;
  image?: GROQImage;
  label?: string;
  widthRem?: number;
}

export interface ResolvedPlatformCard {
  key: PlatformId;
  title: string;
  badgeNew: boolean;
  logos: ResolvedPlatformLogo[];
}

/** Per-asset platform cards; an asset missing here renders the bundled selector. */
export type ResolvedPlatformSelector = Partial<Record<Asset, ResolvedPlatformCard[]>>;

/* ── Resolved bundle ─────────────────────────────────────────────────────── */

export interface ResolvedPricing {
  catalog: Catalog;
  rapidFundedVariants: RapidFundedVariants;
  discountPercent: number;
  discounted: (priceUsd: number) => number;
  checkoutUrl: (presel: string) => string;
  mostPopularSize: SizeId;
  defaultPlatform: Record<Asset, PlatformId>;
  copy: NewPricingCopy;
  /** undefined per asset → the hard-coded selector cards render. */
  platformSelector: ResolvedPlatformSelector;
}

const CODE_DEFAULT_PLATFORM: Record<Asset, PlatformId> = {
  futures: "tradovate",
  cfd_crypto: "mt5",
};

const DEFAULT_CHECKOUT_BASE = "https://checkout.iqcapital.io/products?presel=";

/* ── Catalog mapping ─────────────────────────────────────────────────────── */

/** Marketing-added extra rows: resolve i18n; a picked KPI supplies the label. */
function mapCustomSpecs(
  specs: IV3CustomSpec[] | undefined,
  language: string | undefined,
  kpiLabels: Record<string, string>,
): CustomSpec[] | undefined {
  if (!specs?.length) return undefined;
  return specs.map((sp, i) => ({
    id: sp.kpiKey || resolveI18nText(sp.label, sp.labelI18n, language) || (sp._key ?? String(i)),
    kpiKey: sp.kpiKey || undefined,
    label:
      resolveI18nText(sp.label, sp.labelI18n, language) ||
      (sp.kpiKey ? (kpiLabels[sp.kpiKey] ?? "") : ""),
    value: resolveI18nText(sp.value, sp.valueI18n, language),
  }));
}

type SpecCtx = { language: string | undefined; kpiLabels: Record<string, string> };

function mapChallengeCol(col: IV3ChallengeCol, ctx: SpecCtx): ChallengeCol | null {
  if (!isVariant(col.variant) || !col.presel) return null;
  return {
    variant: col.variant,
    presel: col.presel,
    platforms: col.platforms?.length ? col.platforms : undefined,
    customSpecs: mapCustomSpecs(col.customSpecs, ctx.language, ctx.kpiLabels),
    // Literal numbers: a blank field in the doc renders "—" (no invented
    // defaults — client requirement 2026-09-09).
    data: {
      priceUsd: col.priceUsd ?? undefined,
      profitTargetUsd: col.profitTargetUsd ?? undefined,
      maxDrawdownUsd: col.maxDrawdownUsd ?? undefined,
      maxPositionLossPct: col.maxPositionLossPct ?? undefined,
      dailyLossLimitUsd: col.dailyLossLimitUsd ?? undefined,
      consistencyPct: col.consistencyPct ?? null,
      durationDays: col.durationDays ?? undefined,
      extensionFeeUsd: col.extensionFeeUsd ?? undefined,
    },
  };
}

function mapFundedCol(col: IV3FundedCol, ctx: SpecCtx): FundedCol | null {
  if (!isVariant(col.variant) || !col.presel) return null;
  return {
    variant: col.variant,
    presel: col.presel,
    priceUsd: col.priceUsd ?? undefined,
    platforms: col.platforms?.length ? col.platforms : undefined,
    customSpecs: mapCustomSpecs(col.customSpecs, ctx.language, ctx.kpiLabels),
    // Literal numbers — blank renders "—"; blank fees hide their row.
    data: {
      activationFeeUsd: col.activationFree ? "free" : (col.activationFeeUsd ?? undefined),
      profitSplitTrader: col.profitSplitTrader ?? undefined,
      payoutEveryDays: col.payoutEveryDays ?? undefined,
      maxDrawdownUsd: col.maxDrawdownUsd ?? undefined,
      maxPositionLossPct: col.maxPositionLossPct ?? undefined,
      dailyLossLimitUsd: col.dailyLossLimitUsd ?? undefined,
      consistencyPct: col.consistencyPct ?? null,
      resetFeeUsd: col.resetFeeUsd ?? null,
      extensionFeeUsd: col.extensionFeeUsd ?? undefined,
    },
  };
}

/**
 * Builds the catalog. When the doc carries a catalog it is the LITERAL truth:
 * a product with no sizes there shows no cards, and blank numbers render "—" —
 * nothing silently reverts to the code catalog (client requirement
 * 2026-09-09). The code catalog applies only when the doc has no catalog at
 * all (or there is no doc).
 */
function resolveCatalog(section: IPricingSectionV3 | undefined, ctx: SpecCtx): Catalog {
  if (!section?.catalogAssets?.length) return CATALOG;

  const result = {} as Catalog;
  for (const asset of ASSETS) {
    const assetDoc = section.catalogAssets.find((a) => a.assetKey === asset);
    const products = {} as Record<ProductId, Product>;
    for (const productId of PRODUCTS) {
      const productDoc = assetDoc?.products?.find((p) => p.productKey === productId);
      const sizes: SizeEntry[] = (productDoc?.sizes ?? [])
        .map((s): SizeEntry | null => {
          if (!isSize(s.size)) return null;
          return {
            size: s.size,
            tierName: s.tierName || undefined,
            platforms: (s.platforms ?? []) as PlatformId[],
            challenge: (s.challengeCols ?? [])
              .map((c) => mapChallengeCol(c, ctx))
              .filter((c): c is ChallengeCol => !!c),
            funded: (s.fundedCols ?? [])
              .map((c) => mapFundedCol(c, ctx))
              .filter((c): c is FundedCol => !!c),
          };
        })
        .filter((s): s is SizeEntry => !!s && s.platforms.length > 0);
      const maxAccounts = section.products?.find((p) => p.key === productId)?.maxAccounts;
      products[productId] = { id: productId, maxAccounts, sizes };
    }
    result[asset] = products;
  }
  return result;
}

/* ── Copy merging ────────────────────────────────────────────────────────── */

/**
 * Doc-present semantics: the document is the literal truth — a blank field
 * renders blank (no silent revert to the code copy). Code defaults apply only
 * when there is no document at all (resolveCopy early-returns `base`).
 * The `_fallback` parameter is kept for call-site stability but unused.
 */
function t(
  language: string | undefined,
  value: string | undefined,
  i18n: II18nText | undefined,
  _fallback: string,
): string {
  void _fallback;
  return resolveI18nText(value, i18n, language);
}

const template = (tpl: string, key: string, val: string | number) =>
  tpl.replaceAll(`{${key}}`, String(val));

function resolveCopy(section: IPricingSectionV3 | undefined, language: string | undefined): NewPricingCopy {
  const base = resolveNewPricingCopy(language);
  if (!section) return base;
  const s = section;
  const L = language;

  // Arrays behave like scalars: when the array exists in the doc it is the
  // literal truth (a missing entry renders blank); an entirely absent array
  // means the fieldset was never filled and keeps the code copy.
  const kpi = (key: string) => s.kpiCatalog?.find((k) => k.key === key);
  const kpiLabels = { ...base.kpiLabels };
  const kpiTooltips = { ...base.kpiTooltips };
  if (s.kpiCatalog?.length) {
    for (const key of Object.keys(kpiLabels) as (keyof NewPricingCopy["kpiLabels"])[]) {
      const def = kpi(key);
      kpiLabels[key] = t(L, def?.label, def?.labelI18n, "");
      kpiTooltips[key] = t(L, def?.tooltip, def?.tooltipI18n, "");
    }
  }

  const variantLabel = (key: "mpl" | "dll" | "classic" | "daily") => {
    if (!s.variantLabels?.length) return base.variantLabels[key];
    const def = s.variantLabels.find((v) => v.key === key);
    return t(L, def?.label, def?.labelI18n, "");
  };

  const products = { ...base.products };
  if (s.products?.length) {
    for (const key of PRODUCTS) {
      const def = s.products.find((p) => p.key === key);
      products[key] = {
        name: t(L, def?.name, def?.nameI18n, ""),
        tag: t(L, def?.tag, def?.tagI18n, ""),
        info: t(L, def?.tooltip, def?.tooltipI18n, ""),
      };
    }
  }

  const durationTpl = t(L, s.durationTemplate, s.durationTemplateI18n, "");
  const payoutOneTpl = t(L, s.payoutDaySingular, s.payoutDaySingularI18n, "");
  const payoutManyTpl = t(L, s.payoutDaysTemplate, s.payoutDaysTemplateI18n, "");
  const viewVariantTpl = t(L, s.ctaViewVariantTemplate, s.ctaViewVariantTemplateI18n, "");

  return {
    ...base,
    heroEyebrow: t(L, s.heroEyebrow, s.heroEyebrowI18n, base.heroEyebrow),
    heroTitleLine1: t(L, s.heroTitleLine1, s.heroTitleLine1I18n, base.heroTitleLine1),
    heroTitleAccent: t(L, s.heroTitleAccent, s.heroTitleAccentI18n, base.heroTitleAccent),
    heroSub: t(L, s.heroSub, s.heroSubI18n, base.heroSub),
    markets: {
      futures: t(L, s.marketFuturesLabel, s.marketFuturesLabelI18n, base.markets.futures),
      cfdCrypto: t(L, s.marketCfdCryptoLabel, s.marketCfdCryptoLabelI18n, base.markets.cfdCrypto),
    },
    badgeNew: t(L, s.badgeNew, s.badgeNewI18n, base.badgeNew),
    badgeMostPopular: t(L, s.badgeMostPopular, s.badgeMostPopularI18n, base.badgeMostPopular),
    products,
    stage: {
      challenge: t(L, s.stageChallenge, s.stageChallengeI18n, base.stage.challenge),
      funded: t(L, s.stageFunded, s.stageFundedI18n, base.stage.funded),
      instantOnly: t(L, s.stageInstantOnly, s.stageInstantOnlyI18n, base.stage.instantOnly),
    },
    keyFeatures: t(L, s.keyFeaturesLabel, s.keyFeaturesLabelI18n, base.keyFeatures),
    chosenPath: t(L, s.chosenPathLabel, s.chosenPathLabelI18n, base.chosenPath),
    variantLabels: {
      mpl: variantLabel("mpl"),
      dll: variantLabel("dll"),
      classic: variantLabel("classic"),
      daily: variantLabel("daily"),
    },
    kpiLabels,
    kpiTooltips,
    values: {
      ...base.values,
      none: t(L, s.valueNone, s.valueNoneI18n, base.values.none),
      free: t(L, s.valueFree, s.valueFreeI18n, base.values.free),
      drawdownMode: t(L, s.drawdownModeValue, s.drawdownModeValueI18n, base.values.drawdownMode),
      days: (n: number) => template(durationTpl, "n", n),
      payoutDays: (n: number) => (n === 1 ? payoutOneTpl : template(payoutManyTpl, "n", n)),
    },
    price: {
      ...base.price,
      account: t(L, s.priceAccountLabel, s.priceAccountLabelI18n, base.price.account),
      oneTimeFee: t(L, s.priceOneTimeFeeLabel, s.priceOneTimeFeeLabelI18n, base.price.oneTimeFee),
    },
    footnotes: {
      mpl: t(L, s.footnoteMpl, s.footnoteMplI18n, base.footnotes.mpl),
      dll: t(L, s.footnoteDll, s.footnoteDllI18n, base.footnotes.dll),
    },
    cta: {
      ...base.cta,
      getPlan: t(L, s.ctaGetPlan, s.ctaGetPlanI18n, base.cta.getPlan),
      viewVariant: (label: string) => template(viewVariantTpl, "variant", label),
    },
    unsure: {
      title: t(L, s.unsureTitle, s.unsureTitleI18n, base.unsure.title),
      sub: t(L, s.unsureSub, s.unsureSubI18n, base.unsure.sub),
      helpMeChoose: t(L, s.unsureHelpMeChoose, s.unsureHelpMeChooseI18n, base.unsure.helpMeChoose),
      compare: t(L, s.unsureCompare, s.unsureCompareI18n, base.unsure.compare),
    },
    compare: {
      ...base.compare,
      title: t(L, s.compareTitle, s.compareTitleI18n, base.compare.title),
      sub: t(L, s.compareSub, s.compareSubI18n, base.compare.sub),
      legendMpl: t(L, s.compareLegendMpl, s.compareLegendMplI18n, base.compare.legendMpl),
      legendDll: t(L, s.compareLegendDll, s.compareLegendDllI18n, base.compare.legendDll),
      oneTimeFee: t(L, s.compareOneTimeFee, s.compareOneTimeFeeI18n, base.compare.oneTimeFee),
      challenge: t(L, s.compareChallenge, s.compareChallengeI18n, base.compare.challenge),
      emptySlot: t(L, s.compareEmptySlot, s.compareEmptySlotI18n, base.compare.emptySlot),
      mobileHint: t(L, s.compareMobileHint, s.compareMobileHintI18n, base.compare.mobileHint),
    },
  };
}

/* ── Platform selector ───────────────────────────────────────────────────── */

function resolvePlatformSelector(
  section: IPricingSectionV3 | undefined,
  language: string | undefined,
): ResolvedPlatformSelector {
  const selector: ResolvedPlatformSelector = {};
  for (const assetDoc of section?.selectorAssets ?? []) {
    if (!isAsset(assetDoc.key)) continue;
    const cards = (assetDoc.platforms ?? [])
      .filter((p) => !!p.key)
      .map(
        (p): ResolvedPlatformCard => ({
          key: p.key as PlatformId,
          title: t(language, p.title, p.titleI18n, p.key ?? ""),
          badgeNew: !!p.badgeNew,
          logos: (p.logos ?? []).map((l, i) => ({
            key: l._key ?? String(i),
            image: l.image?.src ? l.image : undefined,
            label: l.label || undefined,
            widthRem: l.widthRem ?? undefined,
          })),
        }),
      );
    if (cards.length) selector[assetDoc.key] = cards;
  }
  return selector;
}

/* ── Entry point ─────────────────────────────────────────────────────────── */

export function resolvePricing(
  section: IPricingSectionV3 | undefined,
  language?: string,
): ResolvedPricing {
  const discountPercent = section?.discountPercent ?? 80;
  const keep = 1 - discountPercent / 100;
  const base = section?.checkoutBaseUrl || DEFAULT_CHECKOUT_BASE;

  const defaultPlatform = { ...CODE_DEFAULT_PLATFORM };
  for (const assetDoc of section?.selectorAssets ?? []) {
    if (isAsset(assetDoc.key) && assetDoc.defaultPlatform) {
      defaultPlatform[assetDoc.key] = assetDoc.defaultPlatform as PlatformId;
    }
  }

  const copy = resolveCopy(section, language);
  return {
    catalog: resolveCatalog(section, { language, kpiLabels: copy.kpiLabels }),
    rapidFundedVariants: section?.rapidFundedVariants ?? RAPID_FUNDED_VARIANTS,
    discountPercent,
    discounted: (priceUsd: number) => Math.round(priceUsd * keep),
    checkoutUrl: (presel: string) => `${base}${presel}`,
    mostPopularSize: isSize(section?.mostPopularSize) ? section.mostPopularSize : "50k",
    defaultPlatform,
    copy,
    platformSelector: resolvePlatformSelector(section, language),
  };
}

/* ── Quiz bridge ─────────────────────────────────────────────────────────── */

/** Quiz product keys (core/rapid/bolt) → catalog product ids. */
const QUIZ_PRODUCT_TO_ID: Record<string, ProductId> = {
  core: "core",
  rapid: "rapid",
  bolt: "instant",
};

/**
 * Availability derived from the resolved catalog — the quiz can only ever
 * recommend a combination the matrix actually sells.
 */
export function catalogPlanAvailability(catalog: Catalog) {
  return (product: string, asset: string, size: string): boolean => {
    const productId = QUIZ_PRODUCT_TO_ID[product];
    if (!productId || !isAsset(asset)) return false;
    return catalog[asset][productId].sizes.some((s) => s.size === size);
  };
}

/** Facts the quiz result card needs, straight from the resolved catalog. */
export function catalogQuizPlan(
  resolved: ResolvedPricing,
  product: string,
  asset: string,
  size: string,
): { title: string; amountLabel: string; assetLabel: string; checkoutHref: string } | null {
  const productId = QUIZ_PRODUCT_TO_ID[product];
  if (!productId || !isAsset(asset)) return null;
  const entry = resolved.catalog[asset][productId].sizes.find((s) => s.size === size);
  if (!entry) return null;
  const presel = entry.challenge[0]?.presel ?? entry.funded[0]?.presel;
  if (!presel) return null;
  return {
    title: resolved.copy.products[productId].name,
    amountLabel: `$${size.toUpperCase()}`,
    assetLabel: asset === "futures" ? resolved.copy.markets.futures : resolved.copy.markets.cfdCrypto,
    checkoutHref: resolved.checkoutUrl(presel),
  };
}
